import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plane, Home, Calendar, DollarSign } from "lucide-react";
import { Button } from "./ui/button";

const navItems = [
  { label: "Accommodations", icon: Home, href: "#accommodations" },
  { label: "Flight Info", icon: Plane, href: "#travel" },
  { label: "Itinerary", icon: Calendar, href: "#itinerary" },
  { label: "Costs", icon: DollarSign, href: "#costs" },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background/80 backdrop-blur-lg shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="container max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`font-serif text-xl font-semibold ${
              scrolled ? "text-foreground" : "text-primary-foreground"
            }`}
          >
            Portugal 2026
          </motion.span>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(true)}
            className={`${scrolled ? "text-foreground" : "text-primary-foreground"} hover:bg-card/20`}
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
      </motion.header>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/60 backdrop-blur-sm z-50"
              onClick={() => setIsOpen(false)}
            />

            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-80 bg-background z-50 shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-12">
                  <span className="font-serif text-2xl font-semibold text-foreground">
                    Navigate
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(false)}
                    className="text-foreground"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {navItems.map((item, index) => (
                    <motion.button
                      key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => scrollToSection(item.href)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-secondary/50 transition-colors group text-left"
                    >
                      <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-medium text-foreground">
                        {item.label}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
